import type { CreatePropertyInput } from '@idc-forms/schemas'
import { CircleOff, CirclePlay, Info, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import {
  INVENTORY_STATUS_COLORS,
  INVENTORY_STATUS_LABELS,
  type InventoryStatus
} from '@/features/inventory/models/inventory'
import { Permission } from '@/features/roles/consts/permissions'
import { formatTypePropertyLabel } from '@/features/type-properties/models/type-property-label'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { cn } from '@/shared/lib/utils'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import {
  useDeletePropertyMutation,
  useUpdatePropertyMutation
} from '../hooks/use-property-mutations'
import type { PropertyAdminRow } from '../models/property-admin'
import { PropertyForm } from './property-form'

/** Alias local: fila estándar de los paneles de detalle (familia compartida). */
const FieldRow = DetailFieldRow

const UNIT_TYPE_LABELS: Record<string, string> = {
  APT: 'Apartamento',
  V: 'Villa',
  C: 'Casa'
}

const STRUCTURE_TYPE_LABELS: Record<string, string> = {
  T: 'Torre',
  V: 'Villa'
}

type PropertyDetailPanelProps = {
  property: PropertyAdminRow
}

/**
 * Panel de detalle del inmueble (catálogo): modo VISTA (resumen + acciones +
 * tab "Más información" con acordeones, espejo del panel de inventario) y modo
 * FORMULARIO in-place para editar (mismo `PropertyForm` que la creación).
 * La disponibilidad/reservas viven en el panel de inventario, no acá.
 */
export function PropertyDetailPanel({ property }: PropertyDetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const updateMutation = useUpdatePropertyMutation()
  const deleteMutation = useDeletePropertyMutation()
  const { has } = usePermissions()

  const statusLabel = INVENTORY_STATUS_LABELS[property.availabilityStatus as InventoryStatus]

  const handleUpdate = (values: CreatePropertyInput) => {
    updateMutation.mutate(
      { id: property._id, payload: values },
      {
        onSuccess: () => {
          setMode('view')
        }
      }
    )
  }

  const toggleActive = () => {
    updateMutation.mutate({ id: property._id, payload: { active: !property.active } })
  }

  if (mode === 'edit') {
    return (
      <PropertyForm
        defaultValues={{
          name: property.name,
          code: property.code,
          project: property.project,
          type_property: property.type_property,
          unitType: property.unitType ?? undefined,
          tipo: property.tipo ?? undefined,
          structureType: property.structureType ?? undefined,
          structureNumber: property.structureNumber ?? undefined,
          identifier: property.identifier ?? undefined,
          constructionM2: property.constructionM2 ?? undefined,
          terrainM2: property.terrainM2 ?? undefined,
          terrainV2: property.terrainV2 ?? undefined,
          terraceM2: property.terraceM2 ?? undefined,
          parkingM2: property.parkingM2 ?? undefined,
          property: property.property ?? undefined,
          folio: property.folio ?? undefined,
          book: property.book ?? undefined,
          waterMeter: property.waterMeter ?? undefined,
          electricityMeter: property.electricityMeter ?? undefined,
          iusiAmount: property.iusiAmount ?? undefined,
          telephone: property.telephone ?? undefined,
          active: property.active
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={handleUpdate}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base font-semibold leading-tight">{property.name}</h3>
            {statusLabel && (
              <span
                className={cn(
                  'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium',
                  INVENTORY_STATUS_COLORS[property.availabilityStatus as InventoryStatus]
                )}
              >
                {statusLabel}
              </span>
            )}
          </div>
          <FieldRow label="Código" value={property.code} copyValue={property.code} />
          <FieldRow label="Proyecto" value={property.projectName} />
          <FieldRow
            label="Subtipo"
            value={formatTypePropertyLabel(property.typePropertyCode, property.typePropertyName)}
          />
          <FieldRow
            label="Tipo de unidad"
            value={property.unitType ? UNIT_TYPE_LABELS[property.unitType] : null}
          />
          <FieldRow label="Activo" value={property.active ? 'Sí' : 'No'} />
          <FieldRow label="En negociación" value={property.inNegotiation ? 'Sí' : 'No'} />
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateProperties)
              ? [
                  {
                    label: 'Editar inmueble',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  {
                    label: property.active ? 'Desactivar inmueble' : 'Activar inmueble',
                    icon: property.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteProperties)
              ? [
                  {
                    label: 'Eliminar inmueble',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['identification']}>
            <AccordionItem value="identification">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Identificación
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <FieldRow label="Tipo" value={property.tipo} />
                  <FieldRow
                    label="Estructura"
                    value={
                      property.structureType ? STRUCTURE_TYPE_LABELS[property.structureType] : null
                    }
                  />
                  <FieldRow label="No. de estructura" value={property.structureNumber} />
                  <FieldRow label="Identificador" value={property.identifier} />
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="construction-data">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Datos de construcción
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <FieldRow label="Construcción (m²)" value={property.constructionM2} />
                  <FieldRow label="Terreno (m²)" value={property.terrainM2} />
                  <FieldRow label="Terreno (v²)" value={property.terrainV2} />
                  <FieldRow label="Terraza (m²)" value={property.terraceM2} />
                  <FieldRow label="Parqueo (m²)" value={property.parkingM2} />
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="additional-data">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Datos adicionales
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <FieldRow label="Finca" value={property.property} />
                  <FieldRow label="Folio" value={property.folio} />
                  <FieldRow label="Libro" value={property.book} />
                  <FieldRow label="Monto IUSI" value={property.iusiAmount} />
                  <FieldRow label="Contador de agua" value={property.waterMeter} />
                  <FieldRow label="Contador de luz" value={property.electricityMeter} />
                  <FieldRow label="Teléfono" value={property.telephone} />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar inmueble</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{property.name}” ({property.code}). Esta acción no se puede deshacer
              desde la interfaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(property._id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
